/*
 * File: abbyCadabbyApi.ts
 * Project: rosita
 * -----
 * Last Modified: 03 Mar 2022 12:22:31 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { ApiMultipleResponse } from 'models/ApiModels';
import { CreateLegalEntityPayload, EditLegalEntity, LegalEntity, LegalEntityParams } from 'models/LegalEntities';
import { ReduxState } from 'redux/store';

const baseUrl = process.env.REACT_APP_MAGGIE_CADABBY_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_MAGGIE_CADABBY;

export const maggieCadabbyApi = createApi({
  reducerPath: 'maggieCadabbyApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as ReduxState).app.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);
        headers.set('Content-Type', 'application/json');
      }

      return headers;
    }
  }),
  tagTypes: ['Company', 'Companies'],
  endpoints: (builder) => ({
    getLegalEntity: builder.query<LegalEntity, string>({
      query: (legalEntities) => ({
        url: `/legalEntities/${legalEntities}`
      }),
      providesTags: ['Company']
    }),
    updateLegalEntity: builder.mutation<LegalEntity, { legalEntityId: any; payload: EditLegalEntity }>({
      query: ({ legalEntityId, payload }) => ({
        url: `/legalEntities/${legalEntityId}`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: ['Company', 'Companies']
    }),
    deleteLegalEntity: builder.mutation<LegalEntity, { legalEntityId: string }>({
      query: ({ legalEntityId }) => ({
        url: `/legalEntities/${legalEntityId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Company', 'Companies']
    }),
    recoverLegalEntity: builder.mutation<LegalEntity, { legalEntityId: string }>({
      query: ({ legalEntityId }) => ({
        url: `/legalEntities/${legalEntityId}/recover`
      }),
      invalidatesTags: ['Company', 'Companies']
    }),
    createLegalEntity: builder.mutation<LegalEntity, { payload: CreateLegalEntityPayload }>({
      query: ({ payload }) => ({
        url: '/legalEntities',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['Companies']
    }),
    getLegalEntities: builder.query<ApiMultipleResponse<LegalEntity>, LegalEntityParams>({
      query: (params) => ({
        url: '/legalEntities/',
        params
      }),
      providesTags: ['Companies']
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const {
  useCreateLegalEntityMutation,
  useGetVersionQuery,
  useGetApiNameQuery,
  useGetLegalEntitiesQuery,
  useGetLegalEntityQuery,
  useUpdateLegalEntityMutation,
  useDeleteLegalEntityMutation,
  useRecoverLegalEntityMutation
} = maggieCadabbyApi;
